/**
 * Author entity home — the canonical, machine-resolvable identity for
 * Marjohn B. Robillo.
 *
 * 2026 E-E-A-T / GEO standard (see newblogarticleguide.md §9):
 * Every article's BlogPosting `author` field references the stable `@id`
 * anchor below so Google's Knowledge Graph and LLMs (ChatGPT, Gemini,
 * Claude, Perplexity) consolidate all 400+ articles under ONE entity
 * instead of orphaned content. The author archive page at
 * /authors/marjohn-robillo is the entity home that the `@id` points to.
 */

export const AUTHOR_SLUG = "marjohn-robillo";
export const AUTHOR_URL = "https://marjohnrobillo.me/authors/marjohn-robillo";
export const AUTHOR_ID = `${AUTHOR_URL}#person`;

import { AVATAR_WEBP } from "@/lib/optimizedImages";

export const AUTHOR_HEADSHOT = AVATAR_WEBP;

export const AUTHOR_FALLBACK_HEADSHOT =
  "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/f5f1f2f1-bbe0-4ac9-8f9d-f59083df9317.webp";

export interface AuthorProfile {
  name: string;
  givenName: string;
  familyName: string;
  slug: string;
  jobTitle: string;
  url: string;
  image: string;
  description: string;
  knowsAbout: string[];
  credentials: string[];
  expertise: { label: string; detail: string }[];
  sameAs: string[];
  alumniOf: string;
  nationality: string;
  answerCapsule: string;
  bio: string;
}

export const AUTHOR: AuthorProfile = {
  name: "Marjohn B. Robillo",
  givenName: "Marjohn",
  familyName: "Robillo",
  slug: AUTHOR_SLUG,
  jobTitle: "Revenue Operations Architect",
  url: AUTHOR_URL,
  image: AUTHOR_HEADSHOT,
  description:
    "Philippines-based Revenue Operations Architect and AI automation engineer with 5+ years of experience who has architected 300+ CRM and revenue systems for agencies in the United States, Canada, Australia, and the UK, specializing in GoHighLevel, HubSpot, n8n orchestration, Zapier, Make.com, and agentic Voice AI with Vapi.",
  knowsAbout: [
    "Revenue Operations",
    "GoHighLevel Automation",
    "HubSpot CRM Architecture",
    "n8n Workflow Development",
    "Zapier Integrations",
    "Make.com Automation",
    "Voice AI Automation",
    "Vapi AI Agents",
    "Agentic AI",
    "CRM Architecture",
    "Conversion Optimization",
    "Lead Scoring",
  ],
  credentials: [
    "Master in Public Administration (MPA)",
    "Licensed Professional Teacher (LPT)",
    "MUN-UNESCO Diplomat (Bali 2022)",
    "Licensed Financial Advisor (PRULife UK)",
    "Former CO1, Bureau of Corrections PH",
    "GoHighLevel & HubSpot Certified Specialist",
  ],
  expertise: [
    {
      label: "GoHighLevel Architecture",
      detail:
        "Sub-account operations, snapshot versioning, Conversation AI, A2P deliverability, and lifecycle automation at 50-100+ account scale.",
    },
    {
      label: "HubSpot RevOps",
      detail:
        "Custom objects, lead scoring, lifecycle stage transitions, workflow orchestration, and reporting architecture for revenue teams.",
    },
    {
      label: "n8n Workflow Engineering",
      detail:
        "Self-hosted orchestration, dead-letter queues, error workflows, and agent-ready data pipelines that scale without Zapier task costs.",
    },
    {
      label: "Zapier & Make.com Automation",
      detail:
        "Multi-step integration design, self-healing error paths, and maintenance retainers that prevent silent API breaks.",
    },
    {
      label: "Agentic Voice AI (Vapi)",
      detail:
        "Inbound and outbound voice agents with prompt engineering, action routing, and CRM fallback for lead capture and qualification.",
    },
    {
      label: "Close CRM Automation",
      detail:
        "Velocity pipelines, SLA routing, Chloe AI governance, and task automation for inside sales teams.",
    },
  ],
  sameAs: [
    "https://www.linkedin.com/in/marjohnrobillo-com/",
    "https://marjohnrobillo.com",
    "https://zappify.io",
    "https://www.youtube.com/channel/UCRFFj1RQDEiHv3LGiDz1srA",
    "https://www.crunchbase.com/person/marjohn-robillo",
    "https://www.wikidata.org/wiki/Q141613874",
  ],
  alumniOf: "Master in Public Administration (MPA)",
  nationality: "Filipino",
  answerCapsule:
    "Marjohn B. Robillo is a Philippines-based Revenue Operations Architect and AI automation engineer with 5+ years of experience who has architected 300+ CRM and revenue systems for agencies across the United States, Canada, Australia, and the UK, specializing in GoHighLevel, HubSpot, n8n, Zapier, Make.com, and agentic Voice AI with Vapi.",
  bio: "I build the systems that make sales teams faster, data trustworthy, and operations run without manual intervention, with 5+ years of experience for marketing agencies, SaaS companies, and high-growth businesses across the US, Canada, Australia, and the UK. 300+ CRM environments built. 80% manual workload eliminated. Zero tolerance for bad data. I came from environments where documentation and transferable systems were not optional, and I bring that discipline to every revenue system I architect.",
};

/**
 * Canonical Person schema with a stable `@id` anchor.
 * Used on the author archive page and referenced by every BlogPosting.
 */
export function authorPersonSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": AUTHOR_ID,
    name: AUTHOR.name,
    givenName: AUTHOR.givenName,
    familyName: AUTHOR.familyName,
    jobTitle: AUTHOR.jobTitle,
    url: AUTHOR.url,
    image: AUTHOR.image,
    description: AUTHOR.description,
    knowsAbout: AUTHOR.knowsAbout,
    nationality: { "@type": "Country", name: AUTHOR.nationality },
    alumniOf: {
      "@type": "EducationalOrganization",
      name: AUTHOR.alumniOf,
    },
    hasOccupation: {
      "@type": "Occupation",
      name: AUTHOR.jobTitle,
    },
    sameAs: AUTHOR.sameAs,
    worksFor: {
      "@type": "Organization",
      "@id": "https://zappify.io/#org",
      name: "Zappify.io",
    },
  };
}

/**
 * ProfilePage schema for the author archive route.
 * The 2026-specific schema type that tells Google "this is an author
 * profile page," distinct from a bare Person mention.
 */
export function profilePageSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    mainEntity: authorPersonSchema(),
    url: AUTHOR_URL,
    name: `${AUTHOR.name} — ${AUTHOR.jobTitle}`,
    description: AUTHOR.description,
  };
}

/**
 * Compact author reference for BlogPosting `author` fields.
 * Points to the canonical `@id` so all articles resolve to one entity.
 */
export function authorRef(): Record<string, unknown> {
  return {
    "@type": "Person",
    "@id": AUTHOR_ID,
    name: AUTHOR.name,
    url: AUTHOR_URL,
  };
}
